// src/components/Toast.js
import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';
import styled from 'styled-components';

// Toast Container
const ToastContainer = styled.div`
  position: fixed;
  bottom: 50%;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  display: flex;
  align-items: center;
  border-radius: 20px;
  padding: 12px 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 280px;
  max-width: 90%;
  backdrop-filter: blur(10px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: slideUp 0.3s forwards;
  border: 1px solid;

  /* Set different colors based on type */
  ${props => props.type === 'success' && `
    background-color: rgba(45, 179, 45, 0.85);
    border-color: rgba(49, 196, 49, 0.8);
    color: white;
  `}
  
  ${props => props.type === 'error' && `
    background-color: rgba(209, 46, 46, 0.85);
    border-color: rgba(232, 50, 50, 0.8);
    color: white;
  `}
  
  ${props => props.type === 'info' && `
    background-color: rgba(46, 111, 209, 0.85);
    border-color: rgba(52, 123, 231, 0.8);
    color: white;
  `}
  
  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translate(-50%, 20px);
    }
    to {
      opacity: 1;
      transform: translate(-50%, 0);
    }
  }
  
  @media (max-width: 600px) {
    bottom: 80px;
    min-width: 200px;
  }
`;

const IconWrapper = styled.div`
  margin-right: 12px;
  display: flex;
  align-items: center;
`;

const MessageWrapper = styled.div`
  flex: 1;
  font-weight: 300;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 4px;
  margin-left: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.8;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 1;
  }
`;

const Toast = ({ message, type = 'info', duration = 3000, onClose }) => {
  const [visible, setVisible] = useState(true);
  
  // Auto-close after duration
  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(false);
      setTimeout(() => {
        onClose && onClose();
      }, 300);
    }, duration);
    
    return () => clearTimeout(timer);
  }, [duration, onClose]);
  
  // Handle manual close
  const handleClose = () => {
    setVisible(false);
    setTimeout(() => {
      onClose && onClose();
    }, 300);
  };
  
  // Determine icon based on type
  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle size={20} />;
      case 'error':
        return <AlertCircle size={20} />;
      case 'info':
      default:
        return <Info size={20} />;
    }
  };
  
  return (
    <ToastContainer 
      type={type} 
      style={{ 
        opacity: visible ? 1 : 0,
        transform: `translate(-50%, ${visible ? 0 : '20px'})`
      }}
    >
      <IconWrapper>
        {getIcon()}
      </IconWrapper>
      <MessageWrapper>
        {message}
      </MessageWrapper>
      <CloseButton onClick={handleClose}>
        <X size={18} />
      </CloseButton>
    </ToastContainer>
  );
};

export default Toast;